import type { InjectionKey, Ref } from 'vue'
import type { Company } from '../Company'
import type {
  AddCompanyRequest,
  ChangeCompanyDetailsRequest,
  GetCompaniesResponse
} from '../api-requests/CompanyRequests'

/**
 * Business-logic commands for the company collection. Each command owns its
 * full write cycle — loading toggle, command-id generation, the API call, and
 * the optimistic cache mutation (with rollback on failure for `add`). Views
 * call these instead of issuing API requests and mutating the cache
 * themselves.
 */
export interface CompanyCommands {
  add(payload: AddCompanyRequest): Promise<void>
  changeDetails(
    companyCode: string,
    payload: ChangeCompanyDetailsRequest
  ): Promise<void>
  deactivate(companyCode: string): Promise<void>
  reactivate(companyCode: string): Promise<void>
}

/**
 * Data source for the collection of all companies.
 *
 * Exposes the full `Company[]` plus a `commands` namespace holding all
 * mutations.
 *
 * Runtime-free contract: the concrete composable additionally returns a
 * `signalREvents` value-object (a `@gat-ship/signalr` type) which is
 * intentionally omitted here to keep `@gat-ship/types` free of runtime
 * imports.
 */
export interface CompanyCollectionService {
  companies: Ref<Company[]>
  findCompany(code: string): Company | undefined
  addCompany(code: string, name: string, description?: string): void
  updateCompany(updatedCompany: Company): void
  removeCompany(code: string): void
  clearData(options?: { unsubscribeFromEvents?: boolean }): void
  fetchCompanies(options?: {
    includeInactive?: boolean
    forceFetch?: boolean
    subscribeToEvents?: boolean
  }): Promise<GetCompaniesResponse | undefined>
  commands: CompanyCommands
}

export const companyCollectionKey = Symbol(
  'gs-company-collection'
) as InjectionKey<CompanyCollectionService>
